import React, { useRef, useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { SearchInput } from './components/SearchInput';
import { SearchFilters } from './components/SearchFilters';
import { SearchSort } from './components/SearchSort';
import { SearchResults } from './components/SearchResults';
import { Pagination } from './components/Pagination';

const searchApi = axios.create({
  baseURL: '/api/dynamic-search',
});

// Helper to read URL parameters
const getUrlParams = () => {
  const params = new URLSearchParams(window.location.search);
  const urlState = {
    keywords: params.get('keywords') || '',
    page: parseInt(params.get('page')) || 0,
    sort: params.get('sort') || '',
    filters: {},
    date_from: params.get('date_from') || '',
    date_to: params.get('date_to') || ''
  };

  // Parse filters from URL
  params.getAll('filters').forEach(filterStr => {
    try {
      const filter = JSON.parse(decodeURIComponent(filterStr));
      Object.entries(filter).forEach(([key, value]) => {
        urlState.filters[key] = value;
      });
    } catch (e) {
      console.error('Error parsing filter from URL:', e);
    }
  });

  return urlState;
};

// Helper to update URL
const updateUrl = (state) => {
  const params = new URLSearchParams();

  if (state.keywords) params.set('keywords', state.keywords);
  if (state.page > 0) params.set('page', state.page.toString());
  if (state.sort) params.set('sort', state.sort);

  // Add date parameters if present
  if (state.date_from) params.set('date_from', state.date_from);
  if (state.date_to) params.set('date_to', state.date_to);

  // Add filters to URL if present
  if (Object.keys(state.filters).length > 0) {
    const filterStr = encodeURIComponent(JSON.stringify(state.filters));
    params.append('filters', filterStr);
  }

  // Update URL without reload
  const newUrl = `${window.location.pathname}${params.toString() ? '?' + params.toString() : ''}`;
  window.history.pushState({}, '', newUrl);
};

const App = ({ config }) => {
  const ref = useRef(null);

  // Get initial state from URL or config
  const urlParams = getUrlParams();

  const [configuredSort, setConfiguredSort] = useState(
    urlParams.sort || config.solr_config.default_sort
  );

  const [searchParams, setSearchParams] = useState({
    keywords: urlParams.keywords || '',
    page: urlParams.page || 0,
    per_page: config.solr_config.results_per_page,
    sort: urlParams.sort || config.solr_config.default_sort,
  });

  // Get initial date state from URL or paragraph config
  const [dateParams, setDateParams] = useState({
    date_from: urlParams.date_from || config.listing_config.date_from || '',
    date_to: urlParams.date_to || config.listing_config.date_to || ''
  });

  // Add initialListingConfig with date field
  const initialListingConfig = {
    ...config.listing_config,
    date_field: 'field_publish_event_date'
  };

  // Update useState for listingConfig
  const [listingConfig, setListingConfig] = useState(initialListingConfig);
  const [selectedFilters, setSelectedFilters] = useState(urlParams.filters || {});

  // Handle browser back/forward
  useEffect(() => {
    const handlePopState = () => {
      const urlParams = getUrlParams();
      setSearchParams(prev => ({
        ...prev,
        keywords: urlParams.keywords || '',
        page: urlParams.page || 0,
        sort: urlParams.sort || config.solr_config.default_sort,
      }));
      setSelectedFilters(urlParams.filters || {});
      setDateParams({
        date_from: urlParams.date_from || '',
        date_to: urlParams.date_to || ''
      });
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [config.solr_config.default_sort]);

  // Update URL when state changes
  useEffect(() => {
    updateUrl({
      keywords: searchParams.keywords,
      page: searchParams.page,
      sort: searchParams.sort,
      filters: selectedFilters,
      date_from: dateParams.date_from,
      date_to: dateParams.date_to
    });
  }, [searchParams, selectedFilters, dateParams]);

  const initialFilters = {};
  Object.entries(config.initial_dataset).forEach(([key, dataset]) => {
    if (dataset.terms?.length > 0) {
      initialFilters[key] = dataset.terms.map(term => term.tid);
    }
  });

  const prepareSearchParams = () => {
    // Start with base search params
    const params = {
      keywords: searchParams.keywords,
      page: searchParams.page,
      per_page: searchParams.per_page,
      sort: searchParams.sort,
      entity_type: config.display_config.entity_config.entity_type,
      bundle: config.display_config.entity_config.bundle,
      initial_dataset: JSON.stringify(config.initial_dataset),
      listing_config: JSON.stringify({
        ...config.listing_config,
        // Only spread dateParams if they have values
        // otherwise it overwrites the paragraph supplied values
        ...(dateParams.date_from || dateParams.date_to ? dateParams : {})
      })
    };

    // Add filters
    params.filters = {};

   // Handle each filter type
  Object.entries(selectedFilters).forEach(([key, values]) => {
    if (key === 'tags' && values && typeof values === 'object') {
      // Send the full structure for tags
      params.filters[key] = {
        groups: values.groups,
        ungrouped: values.ungrouped
      };
    } else if (Array.isArray(values) && values.length > 0) {
      params.filters[key] = values;
    }
  });

  return params;
};

  const { data: results, isLoading, error } = useQuery({
    queryKey: ['search', searchParams, selectedFilters, initialFilters, listingConfig],
    queryFn: async () => {
      const params = prepareSearchParams();

      // console.log('Search params being sent to API:', params);

      // For GET requests below threshold
      if (JSON.stringify(params).length < 1000) {
        const { data } = await searchApi.get('/results', {
          params,
          paramsSerializer: params => {
            const searchParams = new URLSearchParams();

            // Handle non-filter parameters
            Object.entries(params).forEach(([key, value]) => {
              if (key === 'filters') return;
              if (key === 'display_config') {
                Object.entries(value).forEach(([configKey, configValue]) => {
                  if (typeof configValue === 'object') {
                    Object.entries(configValue).forEach(([nestedKey, nestedValue]) => {
                      searchParams.append(`display_config[${configKey}][${nestedKey}]`, nestedValue);
                    });
                  } else {
                    searchParams.append(`display_config[${configKey}]`, configValue);
                  }
                });
              } else {
                searchParams.append(key, typeof value === 'object' ? JSON.stringify(value) : value);
              }
            });

            // Handle filters
            if (params.filters) {
              Object.entries(params.filters).forEach(([key, values]) => {
                if (key === 'tags') {
                  Object.entries(values.groups).forEach(([groupKey, groupValues]) => {
                    groupValues.forEach(value => {
                      searchParams.append(`filters[${key}][groups][${groupKey}][]`, value);
                    });
                  });
                  values.ungrouped.forEach(value => {
                    searchParams.append(`filters[${key}][ungrouped][]`, value);
                  });
                } else if (Array.isArray(values)) {
                  values.forEach(value => {
                    searchParams.append(`filters[${key}][]`, value);
                  });
                }
              });
            }

            const queryString = searchParams.toString();
            // console.log('Generated query string:', queryString);
            return queryString;
          }
        });
        return data;
      }

      // For large requests, ensure JSON stringification
      const postData = {
        ...params,
        initial_dataset: typeof params.initial_dataset === 'string' ?
          params.initial_dataset :
          JSON.stringify(params.initial_dataset),
        listing_config: typeof params.listing_config === 'string' ?
          params.listing_config :
          JSON.stringify(params.listing_config)
      };
      // console.log('POST data:', postData);
      const { data } = await searchApi.post('/results', postData, {
        headers: {
          'Content-Type': 'application/json'
        }
      });
      return data;
    },
  });

  const handleSearch = (keywords) => {
    setSearchParams(prev => ({
      ...prev,
      keywords,
      page: 0,
      // Switch to 'score' if keywords present, otherwise use configured sort
      sort: keywords ? 'score' : configuredSort
    }));
  };

  const handleFilterChange = (newFilters, action) => {
    const actualFilters = {...newFilters};
    const newDateParams = {};

    if (action === 'clear') {
      // Reset everything except sort
      setSearchParams(prev => ({
        ...prev,
        keywords: '',
        page: 0,
        sort: prev.sort // Preserve sort
      }));
      setSelectedFilters({});
      setDateParams({});
      setListingConfig(prev => ({
        ...prev,
        date_field: 'field_publish_event_date'
      }));
      return;
    }

    // Handle date filters
    ['date_from', 'date_to'].forEach(key => {
      if (newFilters[key]) {
        // Convert from dd/mm/yyyy to ISO 8601
        const [day, month, year] = newFilters[key].split('/');
        const isoDate = `${year}-${month}-${day}T00:00:00Z`;
        newDateParams[key] = isoDate;
        delete actualFilters[key];
      }
    });

    // Flatten tags structure for API
    if (actualFilters.tags) {
      // Keep the groups structure intact, but ensure it's properly formatted
      actualFilters.tags = {
        groups: actualFilters.tags.groups || {},
        ungrouped: actualFilters.tags.ungrouped || []
      };
    }

    // Update states
    setSelectedFilters(actualFilters);
    setDateParams(newDateParams);

    // Update listing config with both date params and date field
    setListingConfig(prev => ({
      ...prev,
      date_field: 'field_publish_event_date',
      ...newDateParams
    }));

    // Reset to page 0 when filters change
    setSearchParams(prev => ({
      ...prev,
      page: 0
    }));
  };

  const handleSortChange = (sort) => {
    setConfiguredSort(sort); // Remember user's sort preference
    setSearchParams(prev => ({
      ...prev,
      sort
    }));
  };

  const handlePageChange = (newPage) => {
    setSearchParams(prev => ({ ...prev, page: newPage }));
    ref.current.scrollIntoView()
  };

  let status
  if (error) {
    status = 'Error loading results. Please try again.'
  } else if (isLoading) {
    status = 'Loading...'
  } else if (!results?.items?.length) {
    status = 'No results found'
  } else {
    status = 'Showing results '+(results.metadata.page * results.metadata.per_page + 1).toString()+' to '+(results.metadata.page * results.metadata.per_page + results.items.length).toString()+' of '+results.metadata.total+' results'
  }
  return (
    <div ref={ref} className="dsl-search-section">
      <SearchInput
        value={searchParams.keywords}
        onChange={handleSearch}
      />

      <div className="nsw-layout">
        {/* Left sidebar with filters */}
        <div className="nsw-layout__sidebar">
          <SearchFilters
            listing={config.listing_config}
            filters={config.available_filters}
            selectedFilters={selectedFilters}
            dateParams={dateParams}
            facets={results?.facets}
            onFilterChange={handleFilterChange}
          />
        </div>

        {/* Main content area */}
        <div className="nsw-layout__main">
          <div className="publications-list header nsw-layout">
            <div className="col-header">{status}</div>
            <SearchSort
              options={{
                score: 'Relevance',
                title_asc: 'Alphabetically A-Z',
                title_desc: 'Alphabetically Z-A',
                field_publish_event_date: 'Date newest first',
                field_publish_event_date_value_1: 'Date oldest first'
              }}
              currentSort={searchParams.sort}
              onSortChange={handleSortChange}
            />
          </div>
          {(!results || error) ? undefined : (
            <>
              <SearchResults
                type={config.display_config.entity_config.bundle}
                results={results}
              />

              {results.metadata && (
                <div className="mb-4">
                  <Pagination
                    metadata={results.metadata}
                    onPageChange={handlePageChange}
                  />
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default App;
